import { useEffect, useState } from "react";

import {
  getChatAttachmentUrl,
  getStoredChatFileUrl,
  removeConversationFiles,
  removeStoredChatFiles,
  storeChatFile,
} from "@src/api/lib/chat";
import { chatFilesRevoked } from "@src/api/lib/chatFileOwners";
import { isPreviewableImage } from "./utils";

// Every chat file's state for this page load, shared by the thread and Shared media, so a
// file downloaded in one shows as downloaded in the other. Keyed by message id.
// The files themselves are kept in the browser (see storeChatFile), so after a reload
// they come back from there, not the server.
const urls = new Map(); // downloaded: messageId -> blob URL
const pending = new Map(); // downloading: messageId -> promise of the URL
const progress = new Map(); // messageId -> 0..1, only when the server says how big it is
const listeners = new Set();

const emit = () => listeners.forEach((listener) => listener());

// Re-renders the caller whenever any file's state changes
export const useChatFiles = () => {
  const [, setVersion] = useState(0);
  useEffect(() => {
    const listener = () => setVersion((v) => v + 1);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);
};

export const chatFileUrl = (messageId) => urls.get(messageId);

// Logged out: the files' addresses in this tab go (the files themselves go with clearChatFiles)
export const forgetChatFiles = () => {
  urls.forEach((url) => URL.revokeObjectURL(url));
  urls.clear();
  pending.clear();
  progress.clear();
  emit();
};
// Forgets these files' addresses in this tab (their copies in the browser are dealt with by the caller)
const forgetAddresses = (messageIds) =>
  messageIds.forEach((messageId) => {
    const url = urls.get(messageId);
    if (url) URL.revokeObjectURL(url);
    urls.delete(messageId);
    pending.delete(messageId);
    progress.delete(messageId);
  });
/* ----- Taken out of a chat: every file of it kept for this login goes - those of the messages
   given (on screen, kept in a snapshot) and any other kept from it, found by the ownership
   index (chatFileOwners) - their addresses in this tab and their copies in this browser ----- */
export const forgetConversationFiles = async (conversationId, messageIds = []) => {
  forgetAddresses(messageIds);
  if (messageIds.length) removeStoredChatFiles(messageIds);
  const others = await removeConversationFiles(conversationId);
  forgetAddresses(others);
  emit();
};
export const isDownloadingChatFile = (messageId) => pending.has(messageId);
// A fraction while downloading, or null if the size isn't known (spinner instead)
export const chatFileProgress = (messageId) => progress.get(messageId) ?? null;

// Viewable here: photos the browser can draw, and PDFs (the PDF viewer). Word/Excel/CSV
// can't show in a browser - they're only ever saved.
export const isViewableChatFile = (mimeType = "") =>
  isPreviewableImage(mimeType) || mimeType === "application/pdf";

// Our own file was just sent from this page - reuse it instead of downloading it back. Not for
// a chat taken from me meanwhile
export const rememberChatFile = (messageId, url, file, conversationId) => {
  if (chatFilesRevoked(conversationId)) return;
  urls.set(messageId, url);
  storeChatFile(messageId, file, conversationId);
  emit();
};

// Picks up a file downloaded before (even before a reload) from browser storage - no network
export const findStoredChatFile = async (messageId) => {
  if (urls.has(messageId) || pending.has(messageId)) return;
  const url = await getStoredChatFileUrl(messageId);
  if (url && !urls.has(messageId)) {
    urls.set(messageId, url);
    emit();
  }
};

// Downloads once however many places ask at the same time. conversationId: the chat it's from,
// so the copy kept is noted as that chat's (and a chat taken from me meanwhile keeps nothing)
export const downloadChatFile = (messageId, conversationId) => {
  if (urls.has(messageId)) return Promise.resolve(urls.get(messageId));
  if (!pending.has(messageId)) {
    const request = (async () =>
      (await getStoredChatFileUrl(messageId)) ||
      getChatAttachmentUrl(
        messageId,
        (event) => {
          if (!event.total) return;
          progress.set(messageId, event.loaded / event.total);
          emit();
        },
        conversationId
      ))()
      .then((url) => {
        // Taken out of the chat while it came: not shown
        if (chatFilesRevoked(conversationId)) {
          URL.revokeObjectURL(url);
          throw new Error("No longer in this chat");
        }
        urls.set(messageId, url);
        return url;
      })
      .finally(() => {
        pending.delete(messageId);
        progress.delete(messageId);
        emit();
      });
    pending.set(messageId, request);
    emit();
  }
  return pending.get(messageId);
};

// Saves it through the browser's own download
export const saveChatFile = (url, name) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
};
